// ═══════════════════════════════════════════════════════════════════════════════
// Date Utilities
// ═══════════════════════════════════════════════════════════════════════════════
import { DATE_FORMAT, DATETIME_FORMAT, DATE_REGEX, DATETIME_REGEX } from '../constants/index.js';
/**
 * Format a Date object to YYYY-MM-DD string
 */
export const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
/**
 * Format a Date object to YYYY-MM-DD HH:mm string
 */
export const formatDatetime = (date) => {
    const dateStr = formatDate(date);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${dateStr} ${hours}:${minutes}`;
};
/**
 * Parse a YYYY-MM-DD string to Date object
 * Returns null if invalid
 */
export const parseDate = (dateStr) => {
    if (!DATE_REGEX.test(dateStr)) {
        return null;
    }
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date.getTime())) {
        return null;
    }
    return date;
};
/**
 * Parse a YYYY-MM-DD HH:mm string to Date object
 * Returns null if invalid
 */
export const parseDatetime = (datetimeStr) => {
    if (!DATETIME_REGEX.test(datetimeStr)) {
        return null;
    }
    const [datePart, timePart] = datetimeStr.split(' ');
    const date = new Date(`${datePart}T${timePart}:00`);
    if (isNaN(date.getTime())) {
        return null;
    }
    return date;
};
/**
 * Check if a string is a valid YYYY-MM-DD date
 */
export const isValidDate = (dateStr) => {
    return parseDate(dateStr) !== null;
};
/**
 * Check if a string is a valid YYYY-MM-DD HH:mm datetime
 */
export const isValidDatetime = (datetimeStr) => {
    return parseDatetime(datetimeStr) !== null;
};
/**
 * Get today's date in YYYY-MM-DD format
 */
export const getTodayDate = () => {
    return formatDate(new Date());
};
/**
 * Get current datetime in YYYY-MM-DD HH:mm format
 */
export const getCurrentDatetime = () => {
    return formatDatetime(new Date());
};
/**
 * Get today's date in UTC (for quota reset)
 */
export const getTodayDateUtc = () => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
/**
 * Add days to a date string
 */
export const addDays = (dateStr, days) => {
    const date = parseDate(dateStr);
    if (!date) {
        return dateStr;
    }
    date.setDate(date.getDate() + days);
    return formatDate(date);
};
/**
 * Add minutes to a datetime string
 */
export const addMinutes = (datetimeStr, minutes) => {
    const date = parseDatetime(datetimeStr);
    if (!date) {
        return datetimeStr;
    }
    date.setMinutes(date.getMinutes() + minutes);
    return formatDatetime(date);
};
/**
 * Check if a date is within a range (inclusive)
 */
export const isDateInRange = (dateStr, startDateStr, endDateStr) => {
    const date = parseDate(dateStr);
    const startDate = parseDate(startDateStr);
    const endDate = parseDate(endDateStr);
    if (!date || !startDate || !endDate) {
        return false;
    }
    return date >= startDate && date <= endDate;
};
/**
 * Calculate age from birthday
 */
export const calculateAge = (birthdayStr) => {
    const birthday = parseDate(birthdayStr);
    if (!birthday) {
        return null;
    }
    const today = new Date();
    let age = today.getFullYear() - birthday.getFullYear();
    const monthDiff = today.getMonth() - birthday.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthday.getDate())) {
        age--;
    }
    return age;
};
/**
 * Get day of week (0 = Sunday, 6 = Saturday)
 */
export const getDayOfWeek = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) {
        return null;
    }
    return date.getDay();
};
/**
 * Check if date is today
 */
export const isToday = (dateStr) => {
    return dateStr === getTodayDate();
};
/**
 * Check if datetime is in the past
 */
export const isPast = (datetimeStr) => {
    const date = parseDatetime(datetimeStr);
    if (!date) {
        return false;
    }
    return date < new Date();
};
/**
 * Check if datetime is in the future
 */
export const isFuture = (datetimeStr) => {
    const date = parseDatetime(datetimeStr);
    if (!date) {
        return false;
    }
    return date > new Date();
};
/**
 * Get the next midnight in UTC+0 (for quota reset)
 */
export const getNextUtcMidnight = () => {
    const now = new Date();
    const tomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0));
    return formatDatetime(tomorrow);
};
// Re-export format strings for reference
export { DATE_FORMAT, DATETIME_FORMAT };
//# sourceMappingURL=date.js.map